/**
 * Company Team (Staff) Page
 * HTMX-driven filtering & pagination + the member-edit dynamic modal.
 *
 * See ``.cursor/rules/hx-boost-init.mdc`` for the canonical pattern.
 * In short: ``autoInitTeamList`` can fire several times against the same
 * ``#toggle-filters-btn`` over the lifetime of a session (initial load +
 * every boosted nav landing on /office/settings/team/ + every Back/Forward
 * restoring it from htmx history). Without a per-DOM-node guard, each pass
 * stacks one extra ``click`` listener on the same button → toggling the
 * panel twice (or more) per click → user sees "nothing happens".
 *
 * Edit modal: clicking a member name or the per-row edit pencil opens the
 * canonical dynamic-modal helper (``form-modals.mdc``) against the static
 * ``#team-modal`` shell. The tabbed form partial is fetched from the edit
 * URL; ``team-form.js`` wires its tabs / role toggling / photo upload via
 * ``onContentInjected``. Mirrors ``subcontractor-settings.js``.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { initTeamForm } from './team-form.js';

let _teamModalGlobalBound = false;
let teamModalHelper = null;

function initTeamModal() {
  if (teamModalHelper) return;
  teamModalHelper = createDynamicModal({
    modalId: 'team-modal',
    onSuccess: function (data, helper) {
      if (data && data.message && typeof window.showToast === 'function') {
        window.showToast(data.message, 'success');
      }
      helper.close(function () {
        if (typeof window._softReload === 'function') window._softReload();
      });
    },
    onContentInjected: function (contentEl) {
      // Custom-select wrappers for the role / pay-type / worker-type /
      // subcontractor selects — must run on every inject (initial GET +
      // 422 re-render). See ``form-modals.mdc`` §5.
      if (typeof window.initCustomSelects === 'function') {
        window.initCustomSelects();
      }
      const formEl = contentEl.querySelector('form');
      if (formEl) initTeamForm({ formEl: formEl });
      if (typeof window.reinitFlowbiteWidgets === 'function') {
        const modalEl = document.getElementById('team-modal');
        if (modalEl) window.reinitFlowbiteWidgets(modalEl);
      }
    },
  });
}

function handleTeamModalClick(evt) {
  const t = evt.target;
  if (!t || !t.closest) return;
  const btn = t.closest('[data-team-modal-open]');
  if (!btn) return;
  if (!teamModalHelper) return;
  evt.preventDefault();
  // Edit rows carry ``data-team-edit-url``; the Invite (create) triggers
  // carry ``data-team-modal-url``. Both feed the same modal helper.
  const url = btn.dataset.teamModalUrl || btn.dataset.teamEditUrl;
  if (!url) return;
  const title = btn.dataset.teamModalTitle || 'Edit';
  teamModalHelper.open(url, title);
}

function handleTeamAfterSettle(evt) {
  const target = evt.detail && evt.detail.target;
  if (!target || target.id !== 'main-content') return;
  // The shell node was replaced by a boosted nav / soft reload — the
  // cached helper now points at an orphan. Null it and re-bind to the
  // fresh ``#team-modal``. See ``form-modals.mdc`` §9.
  teamModalHelper = null;
  initTeamModal();
}

function bindTeamModalGlobals() {
  if (_teamModalGlobalBound) return;
  _teamModalGlobalBound = true;
  document.body.addEventListener('click', handleTeamModalClick);
  document.body.addEventListener('htmx:afterSettle', handleTeamAfterSettle);
}

/**
 * Initialize the team list page
 */
export function initTeamList(options = {}) {
  const { hasActiveFilters = false } = options;
  initFilterPanelUI(hasActiveFilters);
  teamModalHelper = null;
  bindTeamModalGlobals();
  initTeamModal();
}

/**
 * Auto-init entry point (wired via ``data-auto-init="teamList"``).
 * Called by ``_runAutoInit`` in app.js on initial DOMContentLoaded, on
 * htmx:afterSwap of #main-content (boosted navigation landing on this
 * page), and on htmx:historyRestore (browser Back/Forward). The page
 * itself swaps its inner ``#list-results`` partial via HTMX, but the root
 * data-auto-init lives on the outer card so its ``data-auto-init-done``
 * flag survives those inner swaps. The global Flowbite re-init is already
 * handled by app.js's ``htmx:afterSwap`` listener.
 */
export function autoInitTeamList() {
  const root = document.querySelector('[data-auto-init="teamList"]');
  if (!root) return;
  initTeamList({
    hasActiveFilters: root.dataset.hasActiveFilters === '1',
  });
}

/**
 * Filter panel toggle / reset (HTMX handles requests).
 *
 * Per-element flags (``_teamFilterBound`` / ``_teamResetBound``) prevent
 * double-binding when ``autoInitTeamList`` fires more than once against
 * the same DOM node — see file header and hx-boost-init.mdc rule 3. Use
 * a plain JS property, NOT ``dataset.*``, because ``dataset.*`` survives
 * outerHTML history snapshots and would falsely report "already bound"
 * on a fresh DOM node restored from cache.
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('teamFilterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  if (toggleBtn && panel && !toggleBtn._teamFilterBound) {
    toggleBtn._teamFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._teamResetBound) {
    resetBtn._teamResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('select.form-select').forEach((sel) => {
        sel.value = '';
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        if (textEl && sel.options[0]) textEl.textContent = sel.options[0].text;
      });

      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

window.initTeamList = initTeamList;
